import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { RefreshCw, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";

const VIX_REGIME_COLOR = {
  FEAR:         "txt-loss",
  CAUTION:      "txt-warn",
  NEUTRAL:      "txt-secondary",
  COMPLACENCY:  "txt-profit",
};

const SENTIMENT_COLOR = {
  BULLISH: "txt-profit",
  BEARISH: "txt-loss",
  NEUTRAL: "txt-secondary",
};

const fmt = (n, d = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

function ChangeChip({ pct }) {
  if (pct == null) return <span className="txt-muted">—</span>;
  const pos = pct >= 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-mono-data ${pos ? "txt-profit" : "txt-loss"}`}>
      {pos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {pos ? "+" : ""}{pct.toFixed(2)}%
    </span>
  );
}

export default function MarketIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/market/intelligence${refresh ? "?refresh=true" : ""}`);
      setData(d);
      if (refresh) toast.success("Market data refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load market intelligence");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const vix = data?.vix;
  const fii = data?.fii_dii;
  const global = data?.global || {};

  return (
    <AppShell>
      <PageHeader
        overline="Market"
        title="Market Intelligence"
        description="India VIX regime, FII/DII institutional flows, and global index pulse. Cached 4 hours."
        actions={
          <Button
            onClick={() => load(true)}
            disabled={loading}
            className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            REFRESH
          </Button>
        }
      />

      <div className="p-8 space-y-6">

        {/* VIX + FII top row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* VIX Panel */}
          <div className="panel p-6 space-y-4">
            <div className="overline mb-1">India VIX</div>
            {!vix ? (
              <div className="txt-muted text-sm">{loading ? "Loading…" : "Data unavailable"}</div>
            ) : (
              <>
                <div className="flex items-end gap-4">
                  <div className="kpi-num text-5xl">{vix.value.toFixed(2)}</div>
                  <div className="mb-1">
                    <ChangeChip pct={vix.change_pct} />
                    <div className={`font-section text-sm mt-1 ${VIX_REGIME_COLOR[vix.regime] || "txt-secondary"}`}>
                      {vix.regime}
                    </div>
                  </div>
                </div>
                {/* Sparkline bars */}
                <div className="flex items-end gap-1 h-12 mt-2">
                  {vix.history.map((h, i) => {
                    const max = Math.max(...vix.history.map(x => x.value));
                    const min = Math.min(...vix.history.map(x => x.value));
                    const range = max - min || 1;
                    const pct = ((h.value - min) / range) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${h.date}: ${h.value}`}>
                        <div
                          className="w-full"
                          style={{ height: `${Math.max(pct, 8)}%`, background: h.value > 20 ? "var(--loss)" : h.value > 14 ? "var(--warn)" : "var(--profit)" }}
                        />
                        <span className="text-[9px] font-mono-data txt-muted">{h.date?.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="text-xs txt-muted pt-2 border-t border-[var(--border)]">
                  VIX &gt; 25 = FEAR &nbsp;·&nbsp; &gt;17 = CAUTION &nbsp;·&nbsp; &gt;12 = NEUTRAL &nbsp;·&nbsp; ≤12 = COMPLACENCY
                </div>
              </>
            )}
          </div>

          {/* FII / DII Panel */}
          <div className="panel p-6 space-y-4">
            <div className="overline mb-1">FII / DII Institutional Flow</div>
            {!fii ? (
              <div className="txt-muted text-sm">{loading ? "Loading…" : "Data unavailable (NSE)"}</div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`font-section text-lg ${SENTIMENT_COLOR[fii.sentiment]}`}>{fii.sentiment}</div>
                  <div className="text-xs txt-muted">FII net today</div>
                </div>
                <div className="space-y-2">
                  {fii.history.map((row, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border-b border-[var(--border)] pb-1 last:border-0">
                      <span className="font-mono-data txt-secondary w-20 shrink-0">{row.date}</span>
                      <div className="flex gap-6">
                        <div className="text-right min-w-[80px]">
                          <div className="overline" style={{ fontSize: "9px" }}>FII NET</div>
                          <span className={`font-mono-data ${row.fii_net > 0 ? "txt-profit" : row.fii_net < 0 ? "txt-loss" : "txt-muted"}`}>
                            {row.fii_net > 0 ? "+" : ""}{fmt(row.fii_net, 0)}Cr
                          </span>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <div className="overline" style={{ fontSize: "9px" }}>DII NET</div>
                          <span className={`font-mono-data ${row.dii_net > 0 ? "txt-profit" : row.dii_net < 0 ? "txt-loss" : "txt-muted"}`}>
                            {row.dii_net > 0 ? "+" : ""}{fmt(row.dii_net, 0)}Cr
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Global Markets */}
        <div className="panel p-6">
          <div className="overline mb-4">Global Markets</div>
          {Object.keys(global).length === 0 ? (
            <div className="txt-muted text-sm">{loading ? "Loading…" : "Data unavailable"}</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {Object.entries(global).map(([name, info]) => (
                <div key={name} className="border border-[var(--border)] p-3">
                  <div className="overline text-[10px] mb-1 truncate">{name}</div>
                  <div className="kpi-num text-xl">{fmt(info.price, name.includes("Bond") || name.includes("Dollar") ? 2 : 0)}</div>
                  <div className="mt-1"><ChangeChip pct={info.change_pct} /></div>
                </div>
              ))}
            </div>
          )}
          {data?.ts && (
            <div className="text-xs txt-muted mt-4 pt-3 border-t border-[var(--border)]">
              Last updated: {data.ts?.slice(0, 19).replace("T", " ")} UTC · refreshes every 4 hours
            </div>
          )}
        </div>

      </div>
    </AppShell>
  );
}
